import { useLatest } from '@dc-shared-web/hooks/utils/useLatest'
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useMemo,
} from 'react'

type IntersectionObserverSingleCallback = (
  entry: IntersectionObserverEntry
) => void

type IntersectionObserverContextType = {
  observe: (
    element: Element,
    callback: IntersectionObserverSingleCallback
  ) => void
  unobserve: (element: Element) => void
}

type Options = { enabled?: boolean }

const IntersectionObserverContext =
  createContext<IntersectionObserverContextType | null>(null)

export default function IntersectionObserverProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const callbacksRef = useRef(
    new WeakMap<Element, IntersectionObserverSingleCallback>()
  )
  const observerRef = useRef(
    new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const callback = callbacksRef.current.get(entry.target)
        callback?.(entry)
      }
    })
  )

  const value = useMemo(
    () => ({
      observe: (
        element: Element,
        callback: IntersectionObserverSingleCallback
      ) => {
        callbacksRef.current.set(element, callback)
        observerRef.current?.observe(element)
      },
      unobserve: (element: Element) => {
        callbacksRef.current.delete(element)
        observerRef.current?.unobserve(element)
      },
    }),
    []
  )

  return (
    <IntersectionObserverContext.Provider value={value}>
      {children}
    </IntersectionObserverContext.Provider>
  )
}

export function useIntersectionObserver() {
  const context = useContext(IntersectionObserverContext)
  if (!context) {
    throw new Error(
      'useIntersectionObserver must be used within an IntersectionObserverProvider'
    )
  }
  return context
}

export function useVisibleCallback<T extends (...args: any[]) => void>( // eslint-disable-line @typescript-eslint/no-explicit-any
  element: Element | null,
  callback: T,
  options?: Options
) {
  const enabled = options?.enabled ?? true

  const { observe, unobserve } = useIntersectionObserver()
  const latestCallback = useLatest(callback)

  useEffect(() => {
    if (!element) return
    if (enabled) {
      observe(element, (entry) => {
        if (entry.isIntersecting) {
          latestCallback.current()
        }
      })
    } else {
      unobserve(element)
    }
    return () => {
      unobserve(element)
    }
  }, [enabled]) // eslint-disable-line react-hooks/exhaustive-deps
}
